import { Button } from '@/components/ui/button';
import { useListAssets } from '@/features/assets/api/list-assets';
import { ImageIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ImagePickerDialog } from './image-picker-dialog';
import type { PickerAsset } from './image-picker-library';

/** Compact preview and existing dialog pattern from the Claire album follow-up. */
export function CompactPhotoPicker({
  assets: candidateAssets,
  selectedIds,
  onConfirm,
  maxCount = 10,
  disabled = false,
  browseLibrary = false,
  showTrigger = true,
  previewCandidates = false,
  open: controlledOpen,
  onOpenChange,
}: {
  assets: PickerAsset[];
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  maxCount?: number;
  disabled?: boolean;
  browseLibrary?: boolean;
  /** Put the chooser action in a surrounding DecisionFooter. */
  showTrigger?: boolean;
  /** Disambiguation cards show the choices before the owner selects one. */
  previewCandidates?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [librarySelection, setLibrarySelection] = useState<PickerAsset[]>([]);
  const candidates = useMemo(
    () => [
      ...new Map(
        [...candidateAssets, ...librarySelection].map((asset) => [
          asset.id,
          asset,
        ])
      ).values(),
    ],
    [candidateAssets, librarySelection]
  );
  const assets = useFreshPhotoUrls(candidates, selectedIds);
  const assetsById = new Map(assets.map((asset) => [asset.id, asset]));
  const selectedAssets = selectedIds.flatMap((id) => {
    const asset = assetsById.get(id);
    return asset ? [asset] : [];
  });
  const previewAssets = previewCandidates ? assets : selectedAssets;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };
  return (
    <>
      <div className="flex items-center gap-2">
        {previewAssets
          .slice(0, previewAssets.length > 4 ? 3 : 4)
          .map((asset, index) => (
            <button
              key={asset.id}
              type="button"
              disabled={disabled}
              aria-label={`Preview photo ${index + 1}`}
              onClick={() => setOpen(true)}
              className="size-14 shrink-0 overflow-hidden rounded-md border bg-muted"
            >
              {asset.thumbnailUrl || asset.blobUrl ? (
                <img
                  src={asset.thumbnailUrl ?? asset.blobUrl ?? undefined}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <ImageIcon className="mx-auto size-5 text-muted-foreground" />
              )}
            </button>
          ))}
        {previewAssets.length > 4 && (
          <button
            type="button"
            disabled={disabled}
            aria-label={`View ${previewAssets.length - 3} more photos`}
            onClick={() => setOpen(true)}
            className="grid size-14 shrink-0 place-items-center rounded-md border bg-muted text-sm"
          >
            +{previewAssets.length - 3}
          </button>
        )}
      </div>
      {showTrigger ? (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {maxCount === 1 ? 'Choose a photo' : 'Choose photos'}
        </Button>
      ) : null}
      {open && (
        <PhotoPickerDialog
          browseLibrary={browseLibrary}
          onSelectionAssets={setLibrarySelection}
          open={open}
          onOpenChange={setOpen}
          assets={assets}
          selectedIds={selectedIds}
          onConfirm={onConfirm}
          maxCount={maxCount}
          minCount={1}
          title={maxCount === 1 ? 'Choose a photo' : 'Choose photos'}
          confirmLabel={
            maxCount === 1 ? 'Use this photo' : 'Use selected photos'
          }
        />
      )}
    </>
  );
}

/**
 * Tool results persist the asset's stored S3 URL, and the bucket is private —
 * so thumbnails painted straight from the tool output were broken images while
 * the "Choose photos" dialog, which reads the library API, showed the same
 * photos fine. Take the display URLs from that same (shared, cached) library
 * query; the candidate set and its order still come from the tool.
 */
function useFreshPhotoUrls(
  assets: PickerAsset[],
  selectedIds: string[]
): PickerAsset[] {
  const { assets: library } = useListAssets({
    type: 'image',
    source: 'raw',
    limit: 100,
    offset: 0,
  });
  return useMemo(() => {
    if (!library.length) return assets;
    const fresh = new Map(library.map((asset) => [asset.id, asset]));
    const candidateIds = new Set(assets.map((asset) => asset.id));
    const selectedIdSet = new Set(selectedIds);
    const selectedLibraryAssets = library.filter(
      (asset) => selectedIdSet.has(asset.id) && !candidateIds.has(asset.id)
    );
    return [...assets, ...selectedLibraryAssets].map((asset) => {
      const match = fresh.get(asset.id);
      return match
        ? {
            ...asset,
            blobUrl: match.blobUrl ?? asset.blobUrl,
            thumbnailUrl: match.thumbnailUrl ?? asset.thumbnailUrl,
          }
        : asset;
    });
  }, [assets, library, selectedIds]);
}

function PhotoPickerDialog(
  props: React.ComponentProps<typeof ImagePickerDialog> & {
    browseLibrary: boolean;
    onSelectionAssets: (assets: PickerAsset[]) => void;
  }
) {
  return props.browseLibrary ? (
    <LibraryPhotoPickerDialog {...props} />
  ) : (
    <ImagePickerDialog {...props} />
  );
}

/** Browse every page; reference/disambiguation dialogs keep their exact candidates. */
function LibraryPhotoPickerDialog(
  props: React.ComponentProps<typeof ImagePickerDialog> & {
    onSelectionAssets: (assets: PickerAsset[]) => void;
  }
) {
  const [offset, setOffset] = useState(0);
  const [loaded, setLoaded] = useState<PickerAsset[]>([]);
  const { assets, total, isLoading, isError, refetch } = useListAssets({
    type: 'image',
    source: 'raw',
    limit: 100,
    offset,
  });
  useEffect(() => {
    if (!assets.length) return;
    setLoaded((previous) => [
      ...new Map(
        [...previous, ...assets].map((asset) => [asset.id, asset])
      ).values(),
    ]);
  }, [assets]);
  return (
    <ImagePickerDialog
      {...props}
      assets={loaded}
      onConfirm={(ids) => {
        const selected = new Set(ids);
        props.onSelectionAssets(
          loaded.filter((asset) => selected.has(asset.id))
        );
        props.onConfirm(ids);
      }}
      libraryPage={{
        loaded: loaded.length,
        total,
        isLoading,
        isError,
        onLoadMore: () => setOffset(offset + 100),
        onRetry: () => {
          void refetch();
        },
      }}
    />
  );
}
